import { useEffect, useState } from 'react';

import { Button, Dialog, Field, Input, Select, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { useSuaLoaiHoSo, useTaoLoaiHoSo } from '../hooks/useThamDinh';
import type { LinhVuc, LoaiHoSo } from '../types';

const tachDong = (v: string): string[] =>
  v.split('\n').map((d) => d.trim()).filter(Boolean);

export interface FormLoaiHoSoProps {
  open: boolean;
  onClose: () => void;
  linhVuc: LinhVuc[];
  dangSua?: Pick<LoaiHoSo, 'id' | 'linh_vuc_id' | 'ten' | 'mo_ta' | 'thanh_phan_mac_dinh'>;
  /** Lĩnh vực chọn sẵn khi tạo từ màn đang lọc theo một lĩnh vực. */
  linhVucMacDinh?: string;
}

export function FormLoaiHoSo({ open, onClose, linhVuc, dangSua, linhVucMacDinh }: FormLoaiHoSoProps) {
  const { t } = useT();
  const dichLoi = useLoi();
  const tao = useTaoLoaiHoSo();
  const sua = useSuaLoaiHoSo();
  const chay = dangSua ? sua : tao;

  const [linhVucId, setLinhVucId] = useState<string | undefined>();
  const [ten, setTen] = useState('');
  const [moTa, setMoTa] = useState('');
  const [thanhPhan, setThanhPhan] = useState('');

  useEffect(() => {
    if (!open) return;
    setLinhVucId(dangSua?.linh_vuc_id ?? linhVucMacDinh);
    setTen(dangSua?.ten ?? '');
    setMoTa(dangSua?.mo_ta ?? '');
    setThanhPhan((dangSua?.thanh_phan_mac_dinh ?? []).join('\n'));
    chay.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dangSua, linhVucMacDinh]);

  const loiTruong = loiTheoTruong(chay.error);
  const loiChung = chay.error && Object.keys(loiTruong).length === 0 ? dichLoi(chay.error) : undefined;

  function luu() {
    if (dangSua) {
      sua.mutate(
        { id: dangSua.id, du: { ten, mo_ta: moTa, thanh_phan_mac_dinh: tachDong(thanhPhan) } },
        { onSuccess: onClose },
      );
    } else if (linhVucId) {
      tao.mutate(
        { linh_vuc_id: linhVucId, ten, mo_ta: moTa, thanh_phan_mac_dinh: tachDong(thanhPhan) },
        { onSuccess: onClose },
      );
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(dangSua ? 'thamDinh.loaiHoSo.formSuaTieuDe' : 'thamDinh.loaiHoSo.formTaoTieuDe')}
    >
      <div className="space-y-4 pt-2">
        {loiChung && (
          <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{loiChung}</p>
        )}

        {/* Lĩnh vực không chuyển được sau khi tạo: căn cứ pháp luật tra theo nguồn của lĩnh vực cũ. */}
        <Field
          label={t('thamDinh.loaiHoSo.nhanLinhVuc')}
          required={!dangSua}
          hint={t('thamDinh.loaiHoSo.moLinhVuc')}
          error={loiTruong['/linh_vuc_id']}
        >
          {(p) => (
            <Select
              {...p}
              className="w-full"
              value={linhVucId}
              disabled={Boolean(dangSua)}
              placeholder={t('thamDinh.loaiHoSo.chonLinhVuc')}
              options={linhVuc.map((lv) => ({ value: lv.id, label: `${lv.ten} (${lv.ma})` }))}
              onChange={(v) => setLinhVucId(v)}
            />
          )}
        </Field>

        <Field label={t('thamDinh.loaiHoSo.nhanTen')} required error={loiTruong['/ten']}>
          {(p) => <Input {...p} value={ten} onChange={(e) => setTen(e.target.value)} />}
        </Field>

        <Field label={t('thamDinh.loaiHoSo.nhanMoTa')} error={loiTruong['/mo_ta']}>
          {(p) => <Textarea {...p} rows={2} value={moTa} onChange={(e) => setMoTa(e.target.value)} />}
        </Field>

        <Field
          label={t('thamDinh.loaiHoSo.nhanThanhPhan')}
          hint={t('thamDinh.loaiHoSo.moThanhPhan')}
          error={loiTruong['/thanh_phan_mac_dinh']}
        >
          {(p) => <Textarea {...p} rows={4} value={thanhPhan} onChange={(e) => setThanhPhan(e.target.value)} />}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={chay.isPending}
            disabled={!ten || (!dangSua && !linhVucId)}
            onClick={luu}
          >
            {t('chung.luu')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
